import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, ArrowUpRight } from "lucide-react";
import { getSeoDashboardStats } from "@/lib/seo-dashboard.functions";

export const Route = createFileRoute("/dashboard/admin/seo")({
  head: () => ({
    meta: [
      { title: "SEO Dashboard, Heallexa Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SeoDashboardPage,
});

type Stats = Awaited<ReturnType<typeof getSeoDashboardStats>>;

type Tone =
  | "blue"
  | "emerald"
  | "amber"
  | "violet"
  | "rose"
  | "cyan"
  | "indigo"
  | "fuchsia"
  | "teal"
  | "orange"
  | "slate";

const TONE: Record<Tone, string> = {
  blue: "border-border bg-card hover:bg-muted/40",
  emerald: "border-border bg-card hover:bg-muted/40",
  amber: "border-border bg-card hover:bg-muted/40",
  violet: "border-border bg-card hover:bg-muted/40",
  rose: "border-border bg-card hover:bg-muted/40",
  cyan: "border-border bg-card hover:bg-muted/40",
  indigo: "border-border bg-card hover:bg-muted/40",
  fuchsia: "border-border bg-card hover:bg-muted/40",
  teal: "border-border bg-card hover:bg-muted/40",
  orange: "border-border bg-card hover:bg-muted/40",
  slate: "border-border bg-card hover:bg-muted/40",
};

function SeoDashboardPage() {
  const { user, loading: authLoading, primaryRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || primaryRole !== "admin") navigate({ to: "/login" });
  }, [authLoading, user, primaryRole, navigate]);

  const load = async () => {
    setRefreshing(true);
    try {
      const s = await getSeoDashboardStats();
      setStats(s);
    } catch (err) {
      console.error("SEO dashboard load failed", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user && primaryRole === "admin") void load();
  }, [authLoading, user, primaryRole]);

  if (authLoading || loading) {
    return (
      <div className="pt-32 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <main className="pt-6 pb-20">
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex items-center justify-end mb-6">
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* Providers + sitemap inventory */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr mb-4">
          <StatCard
            tone="blue"
            label="Total providers"
            value={stats?.providers.total.toLocaleString() ?? "—"}
            hint="All provider records"
            to="/dashboard/admin/listings/all"
          />
          <StatCard
            tone="emerald"
            label="Indexable providers"
            value={stats?.providers.indexable.toLocaleString() ?? "—"}
            hint="Active, approved, not deleted"
            to="/dashboard/admin/listings/all"
          />
          <StatCard
            tone="cyan"
            label="Provider sitemaps"
            value={String(stats?.providers.sitemapCount ?? 0)}
            hint={`${stats?.providers.perSitemap.toLocaleString()} URLs each`}
            href="/sitemap.xml"
          />
          <StatCard
            tone="indigo"
            label="Directory pages"
            value={String(Math.ceil((stats?.providers.indexable ?? 0) / 1000))}
            hint="1,000 links per page"
            href="/doctors/directory"
          />
        </section>

        {/* Landing inventory */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr mb-4">
          <StatCard
            tone="violet"
            label="Landing pages tracked"
            value={stats?.landing.total.toLocaleString() ?? "—"}
            hint="All landing pages"
            href="/landing-pages/directory"
          />
          <StatCard
            tone="teal"
            label="Indexable landing"
            value={stats?.landing.indexable.toLocaleString() ?? "—"}
            hint="With matching providers"
            href="/landing-pages/directory"
          />
          <StatCard
            tone="slate"
            label="Noindex (empty)"
            value={stats?.landing.noindex.toLocaleString() ?? "—"}
            hint="No matching providers"
          />
          <StatCard
            tone="cyan"
            label="Landing sitemaps"
            value={String(stats?.landing.sitemapCount ?? 0)}
            hint={`${stats?.landing.perSitemap.toLocaleString()} URLs each`}
            href="/sitemap.xml"
          />
        </section>

        {/* Engines + content surfaces — unified 4-col grid */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr">
          <StatCard
            tone="amber"
            label="GSC"
            value={stats?.engines.gscSubmissions.toLocaleString() ?? "0"}
            hint={`${stats?.engines.gscAlerts ?? 0} open alerts`}
            to="/dashboard/admin/seo-search-console"
          />
          <StatCard
            tone="orange"
            label="IndexNow"
            value={stats?.engines.indexnow.toLocaleString() ?? "0"}
            hint="URLs submitted"
            to="/dashboard/admin/seo-indexnow"
          />
          <StatCard
            tone="rose"
            label="Competitor pages"
            value={stats?.engines.competitor.toLocaleString() ?? "0"}
            hint="Tracked competitor URLs"
            to="/dashboard/admin/seo-competitor"
          />
          <StatCard
            tone="fuchsia"
            label="Blog articles"
            value={stats?.engines.blogPublished.toLocaleString() ?? "0"}
            hint={`${(stats?.engines.blogTotal ?? 0).toLocaleString()} total drafts + published`}
            to="/dashboard/admin/blog"
          />
          <StatCard
            tone="indigo"
            label="Press"
            value={stats?.engines.press.toLocaleString() ?? "0"}
            hint="Published press articles"
            to="/dashboard/admin/seo-press"
          />
          <StatCard
            tone="emerald"
            label="Programmable pages"
            value={stats?.engines.programmatic.toLocaleString() ?? "0"}
            hint="Programmatic SEO pages"
            to="/dashboard/admin/seo-programmatic"
          />
        </section>
      </div>
    </main>
  );
}

function StatCard({
  label,
  value,
  hint,
  tone,
  to,
  href,
}: {
  label: string;
  value: string;
  hint?: string;
  tone: Tone;
  to?: string;
  href?: string;
}) {
  const clickable = !!(to || href);
  const inner = (
    <div
      className={`relative border p-5 transition-colors h-full min-h-[130px] flex flex-col ${TONE[tone]} ${
        clickable ? "cursor-pointer hover:shadow-sm" : ""
      }`}
    >
      <p className="text-[11px] uppercase tracking-wider font-semibold opacity-80 pr-6">
        {label}
      </p>
      <p className="mt-2 text-[22px] font-bold tracking-tight leading-tight">{value}</p>
      <p className="mt-auto pt-2 text-xs opacity-70 min-h-[1rem]">{hint ?? "\u00A0"}</p>
      {clickable && (
        <ArrowUpRight className="absolute top-3 right-3 h-4 w-4 opacity-60" />
      )}
    </div>
  );
  if (to) return <Link to={to} className="block h-full">{inner}</Link>;
  if (href)
    return (
      <a href={href} target="_blank" rel="noreferrer" className="block h-full">
        {inner}
      </a>
    );
  return inner;
}
